/**
 * Player Intelligence Foundation — projections from match history cards.
 * No parallel stores; consumes StatisticsProvider / playerCareer outputs.
 */

import {
  calculateCareerStatistics,
  computeLongestWinningStreak,
  projectAchievements,
} from "./playerCareer";
import {
  computeActiveDayStreak,
  computeWinStreak,
  formatMatchWhenShort,
} from "../profile/profileDashboardStats";
import { listPlayableSportModules, resolveSportModule } from "../../sports/registry";
import { normalizeSportId, DEFAULT_SPORT } from "../sport/sportTypes";

/** Sport filter sentinel for cross-sport “Overall”. */
export const SPORT_FILTER_OVERALL = "overall";

/**
 * @param {string | null | undefined} sportFilter
 * @returns {string | null} Firestore sportId filter, or null for all sports
 */
export function resolveHistorySportId(sportFilter) {
  if (!sportFilter || sportFilter === SPORT_FILTER_OVERALL) return null;
  return normalizeSportId(sportFilter);
}

/**
 * @param {number | null | undefined} sec
 */
function formatDurationLabel(sec) {
  if (sec == null || !(sec > 0)) return "—";
  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

/**
 * @param {number | null | undefined} ms
 */
function formatDateLabel(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Extend provider career stats with intelligence metrics (first/last, activity days).
 * Does not recompute win/loss — reuses `stats` from StatisticsProvider.
 * @param {object} stats
 * @param {Array<object>} cards newest-first
 */
export function enrichCareerStatistics(stats, cards) {
  const list = cards || [];
  const chronological = [...list].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  const first = chronological[0] || null;
  const last = list[0] || chronological[chronological.length - 1] || null;
  const dayKeys = new Set();
  list.forEach((c) => {
    if (!c.completedAt) return;
    const d = new Date(c.completedAt);
    dayKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  });
  const durations = list
    .map((c) => c.durationSec)
    .filter((n) => typeof n === "number" && n > 0);
  const fastest = durations.length ? Math.min(...durations) : null;
  const longest = durations.length ? Math.max(...durations) : null;

  return {
    ...stats,
    firstMatchAt: first?.completedAt || null,
    firstMatchLabel: formatDateLabel(first?.completedAt),
    lastPlayedAt: last?.completedAt || null,
    lastPlayedLabel: formatDateLabel(last?.completedAt),
    activityDays: dayKeys.size,
    activeDayStreak: computeActiveDayStreak(list),
    currentWinningStreak:
      stats.currentWinningStreak ?? computeWinStreak(list),
    longestWinningStreak:
      stats.longestWinningStreak ?? computeLongestWinningStreak(list),
    fastestMatchSec: fastest,
    fastestMatchLabel: formatDurationLabel(fastest),
    longestMatchSec: longest,
    longestMatchLabel: formatDurationLabel(longest),
  };
}

/**
 * Sport switcher options for a player's history.
 * @param {Array<{ sportId?: string | null }>} cards
 */
export function buildSportSwitcherOptions(cards) {
  const playable = listPlayableSportModules();
  const counts = {};
  (cards || []).forEach((c) => {
    const id = normalizeSportId(c.sportId || DEFAULT_SPORT);
    counts[id] = (counts[id] || 0) + 1;
  });
  const sportsPlayed = playable
    .filter((m) => counts[m.id] > 0)
    .map((m) => ({
      id: m.id,
      label: m.displayName,
      matches: counts[m.id] || 0,
    }));
  // Include known sports with 0 only if player has multi-sport history (keep switcher lean).
  const options = [
    {
      id: SPORT_FILTER_OVERALL,
      label: "Overall",
      matches: (cards || []).length,
    },
    ...sportsPlayed,
  ];
  // If only one sport ever played, still offer Overall + that sport for consistency.
  if (!sportsPlayed.length) {
    const fallback = resolveSportModule(DEFAULT_SPORT);
    options.push({
      id: DEFAULT_SPORT,
      label: fallback?.displayName || "Badminton",
      matches: 0,
    });
  }
  return options;
}

/**
 * Cross-sport summary for Overall profile.
 * @param {Array<object>} cards newest-first
 */
export function buildCrossSportSummary(cards) {
  const bySport = new Map();
  (cards || []).forEach((c) => {
    const id = normalizeSportId(c.sportId || DEFAULT_SPORT);
    if (!bySport.has(id)) {
      const mod = resolveSportModule(id);
      bySport.set(id, {
        sportId: id,
        label: mod?.displayName || id,
        matches: 0,
        wins: 0,
        losses: 0,
        lastPlayedAt: null,
      });
    }
    const row = bySport.get(id);
    row.matches += 1;
    if (c.isWin) row.wins += 1;
    else row.losses += 1;
    const at = c.completedAt || 0;
    if (!row.lastPlayedAt || at > row.lastPlayedAt) row.lastPlayedAt = at;
  });
  const sports = [...bySport.values()].sort((a, b) => b.matches - a.matches);
  const favorite = sports[0] || null;
  return {
    sportsPlayed: sports.length,
    sports,
    favouriteSport: favorite
      ? { sportId: favorite.sportId, label: favorite.label, matches: favorite.matches }
      : null,
    recentSportActivity: sports
      .slice()
      .sort((a, b) => (b.lastPlayedAt || 0) - (a.lastPlayedAt || 0))
      .slice(0, 4)
      .map((s) => ({
        sportId: s.sportId,
        label: s.label,
        lastPlayedAt: s.lastPlayedAt,
        lastPlayedLabel: formatDateLabel(s.lastPlayedAt),
        matches: s.matches,
      })),
  };
}

/**
 * Personal records / milestones computable from history (no claim store).
 * @param {object} stats enriched career stats
 * @param {Array<object>} cards newest-first
 */
export function projectPersonalRecords(stats, cards) {
  const list = cards || [];
  const chronological = [...list].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  /** @type {Array<{ id: string, title: string, detail: string, whenLabel?: string, unlocked: boolean }>} */
  const records = [];

  const pushMilestone = (id, title, detail, at, unlocked) => {
    records.push({
      id,
      title,
      detail,
      whenLabel: at ? formatMatchWhenShort(at) : undefined,
      unlocked: Boolean(unlocked),
    });
  };

  const first = chronological[0];
  pushMilestone(
    "first_match",
    "First match",
    first ? "Career match recorded" : "Play your first match",
    first?.completedAt,
    Boolean(first)
  );

  const matchMilestones = [10, 50, 100];
  matchMilestones.forEach((n) => {
    const hit = chronological[n - 1];
    pushMilestone(
      `matches_${n}`,
      `${n} matches`,
      hit ? `Reached ${n} career matches` : `Reach ${n} matches`,
      hit?.completedAt,
      Boolean(hit)
    );
  });

  const winsChrono = chronological.filter((c) => c.isWin);
  const firstWin = winsChrono[0];
  pushMilestone(
    "first_win",
    "First win",
    firstWin ? "Opened the win column" : "Win your first match",
    firstWin?.completedAt,
    Boolean(firstWin)
  );
  [10, 100].forEach((n) => {
    const hit = winsChrono[n - 1];
    pushMilestone(
      `wins_${n}`,
      `${n} wins`,
      hit ? `${n} career wins` : `Reach ${n} wins`,
      hit?.completedAt,
      Boolean(hit)
    );
  });

  if (stats.longestWinningStreak >= 3) {
    pushMilestone(
      "longest_streak",
      "Longest winning streak",
      `${stats.longestWinningStreak}-match streak`,
      null,
      true
    );
  }

  if (stats.fastestMatchSec != null) {
    pushMilestone(
      "fastest_match",
      "Fastest match",
      stats.fastestMatchLabel,
      null,
      true
    );
  }
  if (stats.longestMatchSec != null && stats.longestMatchSec !== stats.fastestMatchSec) {
    pushMilestone(
      "longest_match",
      "Longest match",
      stats.longestMatchLabel,
      null,
      true
    );
  }

  // Most active week (ISO week with most matches)
  const weekCounts = new Map();
  chronological.forEach((c) => {
    if (!c.completedAt) return;
    const d = new Date(c.completedAt);
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const week =
      Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7) +
      "-" +
      tmp.getUTCFullYear();
    weekCounts.set(week, (weekCounts.get(week) || 0) + 1);
  });
  let bestWeek = 0;
  weekCounts.forEach((n) => {
    if (n > bestWeek) bestWeek = n;
  });
  if (bestWeek >= 3) {
    pushMilestone(
      "most_active_week",
      "Most active week",
      `${bestWeek} matches in one week`,
      null,
      true
    );
  }

  return records.filter((r) => r.unlocked).slice(0, 12);
}

/**
 * Chronological activity feed (newest first for UI).
 * @param {Array<object>} cards newest-first
 * @param {object} stats enriched stats
 * @param {Array<{ id: string, title: string, detail: string }>} [achievements]
 */
export function projectActivityTimeline(cards, stats, achievements = []) {
  const list = cards || [];
  const chronological = [...list].sort((a, b) => (a.completedAt || 0) - (b.completedAt || 0));
  /** @type {Array<{ id: string, kind: string, title: string, detail?: string, at: number, tone: string }>} */
  const events = [];

  chronological.forEach((c, idx) => {
    const opp = c.opponentsLabel || c.opponentsTeamLine || "opponent";
    const sportMod = resolveSportModule(normalizeSportId(c.sportId || DEFAULT_SPORT));
    const sportLabel = sportMod?.displayName || "Match";
    const typeLabel = c.matchTypeLabel || c.matchType || "Match";

    if (idx === 0) {
      events.push({
        id: `first_${c.matchId}`,
        kind: "milestone",
        title: `Played first ${sportLabel} match`,
        detail: typeLabel,
        at: c.completedAt || 0,
        tone: "amber",
      });
    }

    if (c.isWin) {
      events.push({
        id: `win_${c.matchId}`,
        kind: "result",
        title: `Won against ${opp}`,
        detail: [c.scoreSummary, typeLabel].filter(Boolean).join(" · "),
        at: c.completedAt || 0,
        tone: "win",
      });
    } else {
      events.push({
        id: `loss_${c.matchId}`,
        kind: "result",
        title: `Played ${typeLabel}`,
        detail: `vs ${opp}${c.scoreSummary ? ` · ${c.scoreSummary}` : ""}`,
        at: c.completedAt || 0,
        tone: "muted",
      });
    }

    if (c.competitionId) {
      events.push({
        id: `tour_${c.matchId}`,
        kind: "tournament",
        title: "Tournament match",
        detail: c.competitionName || c.roundName || "Competition play",
        at: c.completedAt || 0,
        tone: "purple",
      });
    }
  });

  // Milestone events at the match that unlocked them
  const winsChrono = chronological.filter((c) => c.isWin);
  [10, 50, 100].forEach((n) => {
    const hit = chronological[n - 1];
    if (hit) {
      events.push({
        id: `m${n}_${hit.matchId}`,
        kind: "milestone",
        title: `Reached ${n} matches`,
        at: hit.completedAt || 0,
        tone: "amber",
      });
    }
  });
  [10, 100].forEach((n) => {
    const hit = winsChrono[n - 1];
    if (hit) {
      events.push({
        id: `w${n}_${hit.matchId}`,
        kind: "milestone",
        title: `${n} career wins`,
        at: hit.completedAt || 0,
        tone: "win",
      });
    }
  });

  if (stats.longestWinningStreak >= 3) {
    events.push({
      id: "streak_record",
      kind: "streak",
      title: "New longest streak",
      detail: `${stats.longestWinningStreak}-match winning streak`,
      at: list[0]?.completedAt || Date.now(),
      tone: "win",
    });
  }

  (achievements || []).slice(0, 4).forEach((a) => {
    events.push({
      id: `ach_${a.id}`,
      kind: "achievement",
      title: a.title,
      detail: a.detail,
      at: list[0]?.completedAt || Date.now(),
      tone: "purple",
    });
  });

  // Dedupe by id, newest first, cap
  const seen = new Set();
  return events
    .filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    })
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .slice(0, 24)
    .map((e) => ({
      ...e,
      whenLabel: formatMatchWhenShort(e.at),
    }));
}

/**
 * Resolve statistics via sport module provider (or shared career for Overall).
 * @param {string} sportFilter
 * @param {Array<object>} cards
 */
export function calculateStatsForSportFilter(sportFilter, cards) {
  const list = cards || [];
  if (!sportFilter || sportFilter === SPORT_FILTER_OVERALL) {
    return enrichCareerStatistics(calculateCareerStatistics(list), list);
  }
  const sportId = normalizeSportId(sportFilter);
  const provider =
    resolveSportModule(sportId)?.createStatisticsProvider?.() ||
    resolveSportModule(sportId)?.createStatsProvider?.();
  const base = (provider?.calculateCareerStatistics || calculateCareerStatistics)(list);
  return enrichCareerStatistics(base, list);
}

/**
 * Achievements via provider when sport-specific.
 * @param {string} sportFilter
 * @param {object} stats
 * @param {Array<object>} cards
 */
export function projectAchievementsForSportFilter(sportFilter, stats, cards) {
  if (!sportFilter || sportFilter === SPORT_FILTER_OVERALL) {
    return projectAchievements(stats, cards);
  }
  const sportId = normalizeSportId(sportFilter);
  const provider =
    resolveSportModule(sportId)?.createStatisticsProvider?.() ||
    resolveSportModule(sportId)?.createStatsProvider?.();
  return (provider?.projectAchievements || projectAchievements)(stats, cards);
}

/**
 * Career stat tiles for UI — only include metrics present / meaningful.
 * @param {object} stats
 * @param {{ sportExtras?: Array<{ label: string, value: string | number }> }} [opts]
 */
export function buildCareerStatTiles(stats, opts = {}) {
  if (!stats) return [];
  /** @type {Array<{ label: string, value: string | number }>} */
  const tiles = [
    { label: "Matches played", value: stats.matchesPlayed ?? 0 },
    { label: "Wins", value: stats.wins ?? 0 },
    { label: "Losses", value: stats.losses ?? 0 },
    {
      label: "Win %",
      value: stats.careerWinPct != null ? `${stats.careerWinPct}%` : "—",
    },
    { label: "Current streak", value: stats.currentWinningStreak ?? 0 },
    { label: "Longest streak", value: stats.longestWinningStreak ?? 0 },
    {
      label: "Avg duration",
      value: stats.averageMatchDurationLabel || "—",
    },
    { label: "Last played", value: stats.lastPlayedLabel || "—" },
    { label: "First match", value: stats.firstMatchLabel || "—" },
    { label: "Activity days", value: stats.activityDays ?? 0 },
  ];

  if (stats.gamesWon != null || stats.gamesLost != null) {
    tiles.splice(4, 0, {
      label: "Games W–L",
      value: `${stats.gamesWon ?? 0}–${stats.gamesLost ?? 0}`,
    });
  }
  if (stats.setsWon != null || stats.setsLost != null) {
    tiles.push({
      label: "Sets W–L",
      value: `${stats.setsWon ?? 0}–${stats.setsLost ?? 0}`,
    });
  }
  if (stats.pointsWon != null || stats.pointsLost != null) {
    tiles.push({
      label: "Points W–L",
      value: `${stats.pointsWon ?? 0}–${stats.pointsLost ?? 0}`,
    });
  }
  (opts.sportExtras || []).forEach((x) => tiles.push(x));
  return tiles;
}
